import React, { useState } from 'react';
import { useStyletron } from 'baseui';
import { Spinner } from 'baseui/spinner';
import { Notification, KIND } from 'baseui/notification';
import { Select } from 'baseui/select';
import { Button } from 'baseui/button';
import { Accordion, Panel } from 'baseui/accordion';
import { useAdPlacementConfig } from '../hooks';
import { useAdsConfigService } from '../services';
import AdTypeConfig from './AdTypeConfig';
import DiffModal from './DiffModal';
import { isAdType, isGameType } from './utils';
import { getChanges, hasChanges } from './changeDetection';

const AdPlacementConfigViewer: React.FC = () => {
	const [css] = useStyletron();
	const { config, loading, error } = useAdPlacementConfig();
	const { updateAdPlacementConfig } = useAdsConfigService();
	const [selectedPlacement, setSelectedPlacement] = useState<any[]>([]);
	const [editedConfig, setEditedConfig] = useState<any>(null);
	const [saving, setSaving] = useState(false);
	const [saveError, setSaveError] = useState<string | null>(null);
	const [showDiffModal, setShowDiffModal] = useState(false);

	// Initialize edited config when config loads
	React.useEffect(() => {
		if (config && !editedConfig) {
			setEditedConfig(JSON.parse(JSON.stringify(config)));
		}
	}, [config, editedConfig]);

	const showDiff = () => {
		setShowDiffModal(true);
		setSaveError(null);
	};

	const closeDiffModal = () => {
		setShowDiffModal(false);
		setSaveError(null);
	};

	const saveChanges = async () => {
		if (!editedConfig) return;

		setSaving(true);
		setSaveError(null);

		try {
			await updateAdPlacementConfig(editedConfig, 'Admin User', 'Updated ad placement configuration');
			setShowDiffModal(false);
			window.location.reload();
		} catch (err: any) {
			// Extract specific error message from API response
			let errorMessage = 'Failed to save changes';

			if (err.response?.data) {
				const { message } = err.response.data;
				errorMessage = message || errorMessage;
			} else if (err.message) {
				errorMessage = err.message;
			}

			setSaveError(errorMessage);
		} finally {
			setSaving(false);
		}
	};

	const resetChanges = () => {
		if (config) {
			setEditedConfig(JSON.parse(JSON.stringify(config)));
		}
	};

	const updateConfigValue = (path: string[], value: any) => {
		if (!editedConfig) return;

		const newConfig = JSON.parse(JSON.stringify(editedConfig));
		let current = newConfig;

		// Safely traverse the path, creating objects if they don't exist
		for (let i = 0; i < path.length - 1; i += 1) {
			const key = path[i];
			if (!current[key] || typeof current[key] !== 'object' || Array.isArray(current[key])) {
				current[key] = {};
			}
			current = current[key];
		}

		current[path[path.length - 1]] = value;
		setEditedConfig(newConfig);
	};

	// Generate placement options dynamically from config
	const placementOptions = React.useMemo(() => {
		if (!config?.placements) return [];

		const options = Object.keys(config.placements).map((key) => ({
			label: key.charAt(0).toUpperCase() + key.slice(1), // Capitalize first letter
			value: key
		}));

		return options;
	}, [config?.placements]);

	// Ensure selected value matches options reference
	const normalizedSelectedPlacement = React.useMemo(() => {
		if (!selectedPlacement.length || !placementOptions.length) return [];

		const selectedValue = selectedPlacement[0]?.value;
		const matchingOption = placementOptions.find((option) => option.value === selectedValue);

		return matchingOption ? [matchingOption] : [];
	}, [selectedPlacement, placementOptions]);

	const renderSelectedPlacement = () => {
		if (!selectedPlacement.length || !config?.placements) return null;

		const placementKey = selectedPlacement[0].value;
		const placementData = config.placements[placementKey];

		if (!placementData) return null;

		return (
			<div
				className={css({
					marginTop: '32px',
					padding: '32px',
					backgroundColor: '#ffffff',
					borderRadius: '20px',
					boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
					border: '1px solid #e8e8e8'
				})}>
				<div
					className={css({
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center',
						marginBottom: '32px',
						paddingBottom: '20px',
						borderBottom: '2px solid #f0f0f0'
					})}>
					<h3
						className={css({
							fontSize: '2rem',
							fontWeight: '700',
							color: '#2c3e50',
							margin: '0',
							display: 'flex',
							alignItems: 'center',
							gap: '12px'
						})}>
						<span
							className={css({
								width: '6px',
								height: '32px',
								backgroundColor: '#3498db',
								borderRadius: '3px'
							})}
						/>
						{selectedPlacement[0].label}
					</h3>
					<div className={css({ display: 'flex', gap: '16px' })}>
						<Button
							onClick={resetChanges}
							size='default'
							kind='secondary'
							overrides={{
								BaseButton: {
									style: {
										paddingTop: '12px',
										paddingBottom: '12px',
										paddingLeft: '20px',
										paddingRight: '20px',
										fontSize: '1rem',
										fontWeight: '600',
										borderRadius: '8px'
									}
								}
							}}>
							Reset Changes
						</Button>
						<Button
							onClick={showDiff}
							size='default'
							disabled={!hasChanges(config, editedConfig)}
							overrides={{
								BaseButton: {
									style: {
										paddingTop: '12px',
										paddingBottom: '12px',
										paddingLeft: '20px',
										paddingRight: '20px',
										fontSize: '1rem',
										fontWeight: '600',
										borderRadius: '8px'
									}
								}
							}}>
							Submit Changes
						</Button>
					</div>
				</div>

				{/* Direct Ad Types */}
				<div className={css({ marginBottom: '40px' })}>
					<h4
						className={css({
							fontSize: '1.5rem',
							fontWeight: '600',
							color: '#2c3e50',
							marginBottom: '24px',
							display: 'flex',
							alignItems: 'center',
							gap: '8px'
						})}>
						<span
							className={css({
								width: '4px',
								height: '24px',
								backgroundColor: '#27ae60',
								borderRadius: '2px'
							})}
						/>
						Primary Ad Types
					</h4>
					<div
						className={css({
							display: 'grid',
							gridTemplateColumns: 'repeat(auto-fit, minmax(500px, 1fr))',
							gap: '20px'
						})}>
						{Object.entries(placementData).map(([key, value]: [string, any]) => {
							if (isAdType(key)) {
								return (
									<AdTypeConfig
										key={key}
										adType={key}
										adConfig={value}
										configPath={['placements', placementKey]}
										editedConfig={editedConfig}
										globalConfig={config}
										onUpdate={updateConfigValue}
									/>
								);
							}
							return null;
						})}
					</div>
				</div>

				{/* Game-specific sections - Collapsible */}
				{Object.entries(placementData).some(([key]) => isGameType(key)) && (
					<div>
						<h4
							className={css({
								fontSize: '1.5rem',
								fontWeight: '600',
								color: '#2c3e50',
								marginBottom: '24px',
								display: 'flex',
								alignItems: 'center',
								gap: '8px'
							})}>
							<span
								className={css({
									width: '4px',
									height: '24px',
									backgroundColor: '#e74c3c',
									borderRadius: '2px'
								})}
							/>
							Game-Specific Configurations
						</h4>
						<Accordion
							overrides={{
								Root: {
									style: {
										border: 'none'
									}
								}
							}}>
							{Object.entries(placementData).map(([key, value]: [string, any]) => {
								if (isGameType(key)) {
									return (
										<Panel
											key={key}
											title={`${key.toUpperCase()} Game`}
											overrides={{
												Header: {
													style: {
														fontSize: '1.2rem',
														fontWeight: '600',
														color: '#495057',
														backgroundColor: '#ffffff',
														borderRadius: '12px',
														border: '2px solid #e8e8e8',
														marginBottom: '12px',
														padding: '20px 24px',
														':hover': {
															backgroundColor: '#f8f9fa',
															borderColor: '#3498db'
														}
													}
												},
												Content: {
													style: {
														backgroundColor: 'transparent',
														padding: '0',
														border: 'none'
													}
												}
											}}>
											<div className={css({ padding: '0 0 24px 0' })}>
												<div
													className={css({
														display: 'grid',
														gridTemplateColumns: 'repeat(auto-fit, minmax(500px, 1fr))',
														gap: '20px'
													})}>
													{Object.entries(value).map(([adType, adConfig]: [string, any]) => {
														if (isAdType(adType)) {
															return (
																<AdTypeConfig
																	key={adType}
																	adType={adType}
																	adConfig={adConfig}
																	configPath={['placements', placementKey, key]}
																	editedConfig={editedConfig}
																	globalConfig={config}
																	onUpdate={updateConfigValue}
																/>
															);
														}
														return null;
													})}
												</div>
											</div>
										</Panel>
									);
								}
								return null;
							})}
						</Accordion>
					</div>
				)}
			</div>
		);
	};

	if (loading) {
		return (
			<div
				className={css({
					display: 'flex',
					justifyContent: 'center',
					alignItems: 'center',
					height: '100vh',
					backgroundColor: '#f8f9fa'
				})}>
				<div className={css({ textAlign: 'center' })}>
					<Spinner />
					<div
						className={css({
							marginTop: '16px',
							fontSize: '1.1rem',
							color: '#6c757d'
						})}>
						Loading configuration...
					</div>
				</div>
			</div>
		);
	}

	if (error) {
		return (
			<div className={css({ padding: '32px', backgroundColor: '#f8f9fa', minHeight: '100vh' })}>
				<div className={css({ maxWidth: '600px', margin: '0 auto' })}>
					<Notification kind={KIND.negative}>{error}</Notification>
				</div>
			</div>
		);
	}

	return (
		<div
			className={css({
				padding: '32px',
				backgroundColor: '#f8f9fa',
				minHeight: '100vh'
			})}>
			<div
				className={css({
					maxWidth: '1400px',
					margin: '0 auto'
				})}>
				<div
					className={css({
						marginBottom: '40px',
						textAlign: 'center'
					})}>
					<h1
						className={css({
							fontSize: '3rem',
							fontWeight: '800',
							color: '#2c3e50',
							marginBottom: '12px',
							letterSpacing: '-0.02em'
						})}>
						Ad Placement Configuration
					</h1>
				</div>

				<div
					className={css({
						marginBottom: '40px',
						display: 'flex',
						justifyContent: 'center'
					})}>
					<div
						className={css({
							maxWidth: '600px',
							width: '100%'
						})}>
						<Select
							options={placementOptions}
							value={normalizedSelectedPlacement}
							placeholder='Select a placement to configure...'
							onChange={(params) => {
								setSelectedPlacement(params.value as any[]);
							}}
							clearable={false}
							valueKey='value'
							labelKey='label'
							overrides={{
								Root: {
									style: {
										fontSize: '1.1rem'
									}
								},
								ControlContainer: {
									style: {
										backgroundColor: '#ffffff',
										borderRadius: '12px',
										border: '2px solid #e9ecef',
										minHeight: '56px',
										boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
										':hover': {
											borderColor: '#3498db',
											boxShadow: '0 6px 20px rgba(52, 152, 219, 0.15)'
										},
										':focus-within': {
											borderColor: '#3498db',
											boxShadow: '0 0 0 4px rgba(52, 152, 219, 0.1)'
										}
									}
								},
								Placeholder: {
									style: {
										color: '#6c757d',
										fontSize: '1.1rem',
										fontWeight: '500'
									}
								}
							}}
						/>
					</div>
				</div>
			</div>

			{renderSelectedPlacement()}

			{/* Diff Modal */}
			<DiffModal
				isOpen={showDiffModal}
				onClose={closeDiffModal}
				onSave={saveChanges}
				saving={saving}
				saveError={saveError}
				changes={getChanges(config, editedConfig)}
				config={config}
				editedConfig={editedConfig}
				hasChanges={() => hasChanges(config, editedConfig)}
			/>
		</div>
	);
};

export default AdPlacementConfigViewer;
